import { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Input, Radio, TimePicker, Select, DatePicker } from 'antd';
import datePickerLocale from 'antd/es/date-picker/locale/zh_CN';
import { eed_web_icon_global_03_action_xialaxiao_area_log } from '@/assets/icons/backup';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import intl from '@/internationalzation/intl';
import { Icon, Message } from '@docs/kd-d2c-components';
import SchedulerIcon from '@src/assets/images/scheduler-card-icon.png';
import ConfirmDeleteModal from '../ConfirmDeleteModal/ConfirmDeleteModal';

const DEFAULT_NAME = 'mario-operation-scheduler-modal';

import './OperationSchedulerModal.less';
import {
  parseTaskForSchedule,
  type ParsedScheduleFromTask,
} from '../parseTaskForSchedule';

const REPEAT_OPTIONS = [
  { value: 'once', label: intl.t('不重复') },
  { value: 'every_day', label: intl.t('每天') },
  { value: 'every_week', label: intl.t('每周') },
  { value: 'every_month', label: intl.t('每月') },
] as const;

const WEEK_OPTIONS = [
  { value: 1, label: intl.t('周一') },
  { value: 2, label: intl.t('周二') },
  { value: 3, label: intl.t('周三') },
  { value: 4, label: intl.t('周四') },
  { value: 5, label: intl.t('周五') },
  { value: 6, label: intl.t('周六') },
  { value: 7, label: intl.t('周日') },
];

const WEEK_VALUE_TEXT: Record<number, string> = {
  1: '一',
  2: '二',
  3: '三',
  4: '四',
  5: '五',
  6: '六',
  7: '日',
};

// 每月中的日期选项（1-31 日）
const MONTH_OPTIONS = Array.from({ length: 31 }, (_, index) => {
  const day = index + 1;
  return { value: day, label: String(day) };
});

const getDefaultTriggerTime = () => dayjs().second(0).millisecond(0);
const getDefaultExpireTime = () => getDefaultTriggerTime().add(1, 'year');

// 不重复执行日期+时间：选今天时禁用此时此分之前的时间
const getDisabledTriggerDateTime = (date: Dayjs | null) => {
  if (!date) return {};
  const now = dayjs();
  if (!date.isSame(now, 'day')) return {};
  const currentHour = now.hour();
  const currentMinute = now.minute();
  return {
    disabledHours: () => Array.from({ length: currentHour }, (_, i) => i),
    disabledMinutes: (selectedHour: number) => {
      if (selectedHour > currentHour) return [];
      if (selectedHour < currentHour) return [];
      return Array.from({ length: currentMinute }, (_, i) => i);
    },
  };
};

export type SchedulerRepeatType =
  | 'once'
  | 'every_day'
  | 'every_week'
  | 'every_month';

export interface TriggerConfig {
  triggerType: SchedulerRepeatType;
  triggerTime: Dayjs | null;
  triggerDate: Dayjs | null;
  weekdays?: number[];
  daysOfMonth?: number[];
}

export interface SchedulerFormValues {
  /** 可选，仅编辑时存在 */
  configId?: string;
  /** 任务名称，20 字以内 */
  title: string;
  /** 提示词内容 */
  task: string;
  /** 触发配置 */
  triggerConfig: TriggerConfig;
  /** 是否启用 */
  enabled: boolean;
  /** 下次运行时间 */
  nextRunTime?: Dayjs | string | number | null;
  /** 结束时间默认一年后 */
  expireTime?: Dayjs | string | number | null;
  /** 创建时间 */
  createTime?: Dayjs | string | number | null;
  /** 更新时间 */
  updateTime?: Dayjs | string | number | null;
}

export interface OperationSchedulerModalProps {
  /** 初始表单值 */
  initialValues?: Partial<SchedulerFormValues>;
  onClose?: () => void;
  onConfirm?: (values: SchedulerFormValues) => void;
  onDelete?: () => void;
}

const defaultInitialValues: Partial<SchedulerFormValues> = {
  triggerConfig: {
    triggerType: 'once',
    triggerTime: getDefaultTriggerTime(),
    triggerDate: null,
    weekdays: [],
    daysOfMonth: [],
  },
  enabled: true,
  expireTime: getDefaultExpireTime(),
};

const OperationSchedulerModal = (props: OperationSchedulerModalProps) => {
  const {
    initialValues,
    onClose,
    onConfirm,
    onDelete,
  } = props;

  const [title, setTitle] = useState(
    () => initialValues?.title ?? defaultInitialValues.title ?? '',
  );
  const [task, setTask] = useState(
    () => initialValues?.task ?? defaultInitialValues.task ?? '',
  );
  const [triggerType, setTriggerType] = useState<SchedulerRepeatType>(
    () =>
      initialValues?.configId != null
        ? (initialValues?.triggerConfig?.triggerType ?? defaultInitialValues.triggerConfig?.triggerType ?? 'once')
        : 'once',
  );
  const [triggerTime, setTriggerTime] = useState<Dayjs | null>(
    () =>
      initialValues?.triggerConfig?.triggerTime ??
      defaultInitialValues.triggerConfig?.triggerTime ??
      getDefaultTriggerTime(),
  );
  const [triggerDate, setTriggerDate] = useState<Dayjs | null>(
    () =>
      initialValues?.triggerConfig?.triggerDate ??
      (initialValues?.configId == null ? dayjs().startOf('day') : null) ??
      defaultInitialValues.triggerConfig?.triggerDate ??
      null,
  );
  const [weekDays, setWeekDays] = useState<number[]>(
    () => initialValues?.triggerConfig?.weekdays ?? defaultInitialValues.triggerConfig?.weekdays ?? [],
  );
  const [daysOfMonth, setDaysOfMonth] = useState<number[]>(
    () => initialValues?.triggerConfig?.daysOfMonth ?? defaultInitialValues.triggerConfig?.daysOfMonth ?? [],
  );
  const [enabled, setEnabled] = useState(
    () => initialValues?.enabled ?? defaultInitialValues.enabled ?? true,
  );
  const [nextRunTime, setNextRunTime] = useState<Dayjs | string | number | null>(
    () => initialValues?.nextRunTime ?? defaultInitialValues.nextRunTime ?? null,
  );
  const [createTime, setCreateTime] = useState<Dayjs | string | number | null>(
    () => initialValues?.createTime ?? defaultInitialValues.createTime ?? null,
  );
  const [updateTime, setUpdateTime] = useState<Dayjs | string | number | null>(
    () => initialValues?.updateTime ?? null,
  );
  const [expireTime, setExpireTime] = useState<Dayjs | string | number | null>(
    () =>
      initialValues?.expireTime ??
      defaultInitialValues.expireTime ??
      getDefaultExpireTime(),
  );
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const configId = initialValues?.configId;
  const taskParseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 提示词中含时间时，解析并自动填充重复类型、执行时间、星期/每月日期、到期时间
  const applyParsedTask = useCallback((parsed: Partial<ParsedScheduleFromTask>) => {
    if (parsed.triggerType != null) setTriggerType(parsed.triggerType);
    if (parsed.triggerTime !== undefined) setTriggerTime(parsed.triggerTime ?? null);
    if (parsed.triggerDate !== undefined) setTriggerDate(parsed.triggerDate ?? null);
    if (parsed.weekdays != null) setWeekDays(parsed.weekdays);
    if (parsed.daysOfMonth != null) setDaysOfMonth(parsed.daysOfMonth);
    if (parsed.expireTime !== undefined) setExpireTime(parsed.expireTime ?? null);
  }, []);

  // 仅新建时根据提示词解析自动填充；编辑时不覆盖已加载数据
  useEffect(() => {
    if (configId != null || !task.trim()) return;
    if (taskParseTimerRef.current) clearTimeout(taskParseTimerRef.current);
    console.log('task', task);
    taskParseTimerRef.current = setTimeout(() => {
      const parsed = parseTaskForSchedule(task);
      if (Object.keys(parsed).length > 0) applyParsedTask(parsed);
      taskParseTimerRef.current = null;
    }, 400);
    return () => {
      if (taskParseTimerRef.current) clearTimeout(taskParseTimerRef.current);
    };
  }, [task, configId, applyParsedTask]);

  useEffect(() => {
    if (initialValues) {
      setTitle(initialValues.title ?? '');
      setTask(initialValues.task ?? '');
      setTriggerType(
        initialValues.configId != null
          ? (initialValues.triggerConfig?.triggerType ?? 'once')
          : 'once',
      );
      setTriggerTime(
        initialValues.triggerConfig?.triggerTime ?? getDefaultTriggerTime(),
      );
      setTriggerDate(
        initialValues.triggerConfig?.triggerDate ??
        (initialValues.configId == null ? dayjs().startOf('day') : null),
      );
      setWeekDays(initialValues.triggerConfig?.weekdays ?? []);
      setDaysOfMonth(initialValues.triggerConfig?.daysOfMonth ?? []);
      setEnabled(initialValues.enabled ?? defaultInitialValues.enabled ?? true);
      setNextRunTime(initialValues.nextRunTime ?? null);
      setExpireTime(initialValues.expireTime ?? getDefaultExpireTime());
      setCreateTime(initialValues.createTime ?? null);
      setUpdateTime(initialValues.updateTime ?? null);
    }
  }, [initialValues]);

  const handleClose = () => {
    onClose?.();
  };

  const handleConfirm = () => {
    const trimmedTitle = title.trim();
    const trimmedTask = task.trim();
    if (!trimmedTitle) {
      Message.error({ message: intl.t('请输入标题') });
      return;
    }
    if (!trimmedTask) {
      Message.error({ message: intl.t('请输入提示词') });
      return;
    }
    if (triggerType === 'every_week' && (!weekDays?.length)) {
      Message.error({ message: intl.t('请选择星期') });
      return;
    }
    if (triggerType === 'every_month' && (!daysOfMonth?.length)) {
      Message.error({ message: intl.t('请选择日期') });
      return;
    }
    if (triggerType === 'once') {
      if (triggerDate == null || triggerTime == null) {
        Message.error({ message: intl.t('请选择执行日期') });
        return;
      }
      const executionMoment = triggerDate
        .hour(triggerTime.hour())
        .minute(triggerTime.minute())
        .second(0)
        .millisecond(0);
      if (!executionMoment.isAfter(dayjs())) {
        Message.error({ message: intl.t('执行时间须在当前时间之后') });
        return;
      }
    } else {
      if (triggerTime == null) {
        Message.error({ message: intl.t('请选择执行时间') });
        return;
      }
    }
    onConfirm?.({
      configId,
      title: trimmedTitle,
      task: trimmedTask,
      triggerConfig: {
        triggerType,
        triggerTime,
        triggerDate,
        weekdays: weekDays,
        daysOfMonth: daysOfMonth,
      },
      enabled,
      nextRunTime,
      expireTime,
      createTime,
      updateTime,
    });
    handleClose();
  };

  const handleDeleteClick = () => {
    setShowConfirmDelete(true);
  };

  const handleDeleteConfirm = () => {
    onDelete?.();
    setShowConfirmDelete(false);
    handleClose();
  };

  return (
    <div className={`${DEFAULT_NAME}`} onClick={(e) => e.stopPropagation()}>
      <div className={`${DEFAULT_NAME}-header`}>
        <img
          className={`${DEFAULT_NAME}-header-icon`}
          src={SchedulerIcon}
          alt="scheduler-icon"
        />
        <div className={`${DEFAULT_NAME}-header-title`}>
          {configId ? intl.t('编辑定时任务') : intl.t('添加定时任务')}
        </div>
      </div>

      <div className={`${DEFAULT_NAME}-body`}>
        <div className={`${DEFAULT_NAME}-field`}>
          <span className={`${DEFAULT_NAME}-label`}>
            {intl.t('标题 *')}
          </span>
          <Input
            className={`${DEFAULT_NAME}-input`}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={intl.t('如: 收集AI新闻')}
            maxLength={20}
            allowClear={false}
          />
        </div>

        <div className={`${DEFAULT_NAME}-field`}>
          <span className={`${DEFAULT_NAME}-label`}>
            {intl.t('提示词 *')}
          </span>
          <Input.TextArea
            className={`${DEFAULT_NAME}-textarea`}
            value={task}
            onChange={(e) => setTask(e.target.value)}
            placeholder={intl.t('描述定时任务要执行的内容')}
            rows={4}
            allowClear={false}
          />
        </div>

        <div className={`${DEFAULT_NAME}-field`}>
          <span className={`${DEFAULT_NAME}-label`}>
            {intl.t('重复时间')}
          </span>
          <div className={`${DEFAULT_NAME}-repeat-row`}>
            <Radio.Group
              className={`${DEFAULT_NAME}-repeat-radio-group`}
              value={triggerType}
              onChange={(e) => {
                const newType = (e.target.value as SchedulerRepeatType) ?? 'once';
                setTriggerType(newType);
                if (newType !== 'every_week') setWeekDays([]);
                if (newType !== 'every_month') setDaysOfMonth([]);
              }}
            >
              {REPEAT_OPTIONS.map((o) => (
                <Radio
                  key={o.value}
                  value={o.value}
                  className={`${DEFAULT_NAME}-repeat-radio`}
                >
                  {o.label}
                </Radio>
              ))}
            </Radio.Group>
            <div className={`${DEFAULT_NAME}-repeat-time-row`}>
              {triggerType === 'once' && (
                <DatePicker
                  className={`${DEFAULT_NAME}-trigger-datepicker`}
                  value={
                    triggerDate != null && triggerTime != null
                      ? triggerDate.hour(triggerTime.hour()).minute(triggerTime.minute()).second(0).millisecond(0)
                      : triggerDate ?? null
                  }
                  onChange={(d) => {
                    if (d) {
                      setTriggerDate(d);
                      setTriggerTime(d);
                    }
                  }}
                  format="YYYY-MM-DD HH:mm"
                  placeholder={intl.t('选择执行日期')}
                  allowClear={false}
                  showNow={false}
                  needConfirm={false}
                  locale={datePickerLocale}
                  defaultValue={dayjs(Date.now())}
                  showTime={{ showNow: false, defaultOpenValue: getDefaultTriggerTime() }}
                  disabledDate={(d) => d != null && d.endOf('day').isBefore(dayjs().startOf('day'))}
                  disabledTime={getDisabledTriggerDateTime}
                  suffixIcon={<Icon icon={eed_web_icon_global_03_action_xialaxiao_area_log} size={16} />}
                  getPopupContainer={() => document.getElementsByClassName(`${DEFAULT_NAME}-trigger-datepicker`)[0] as HTMLElement}
                />
              )}
              {triggerType === 'every_week' && (
                <Select
                  mode="multiple"
                  className={`${DEFAULT_NAME}-week-select`}
                  placeholder={intl.t('选择星期')}
                  value={weekDays}
                  onChange={(vals) => setWeekDays(vals as number[])}
                  options={WEEK_OPTIONS}
                  showSearch={false}
                  suffixIcon={
                    <Icon
                      icon={eed_web_icon_global_03_action_xialaxiao_area_log}
                      size={16}
                    />
                  }
                  // 将所有选中星期汇总成一串文本：一、二、三、五…
                  tagRender={({ value }) => {
                    const selected = [...weekDays].sort((a, b) => a - b);
                    if (!selected.length) {
                      return (
                        <span className={`${DEFAULT_NAME}-week-tag`} />
                      );
                    }

                    const first = selected[0];
                    const num = Number(value);
                    // 只用第一个值代表整体，其它 tag 不渲染
                    if (num !== first) {
                      return (
                        <span className={`${DEFAULT_NAME}-week-tag`} />
                      );
                    }

                    const text = selected
                      .map((v) => WEEK_VALUE_TEXT[v] ?? String(v))
                      .join('、');

                    return (
                      <span
                        className={`${DEFAULT_NAME}-week-tag`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        {text}
                      </span>
                    );
                  }}
                />
              )}
              {triggerType === 'every_month' && (
                <Select
                  mode="multiple"
                  className={`${DEFAULT_NAME}-month-select`}
                  placeholder={intl.t('选择日期')}
                  value={daysOfMonth}
                  onChange={(vals) => setDaysOfMonth(vals as number[])}
                  options={MONTH_OPTIONS}
                  showSearch={false}
                  suffixIcon={
                    <Icon
                      icon={eed_web_icon_global_03_action_xialaxiao_area_log}
                      size={16}
                    />
                  }
                  // 将所有选中月份汇总成一串文本：1、2、3、5…
                  tagRender={({ value }) => {
                    const selected = [...daysOfMonth].sort((a, b) => a - b);
                    if (!selected.length) {
                      return (
                        <span className={`${DEFAULT_NAME}-month-tag`} />
                      );
                    }

                    const first = selected[0];
                    const num = Number(value);
                    if (num !== first) {
                      return (
                        <span className={`${DEFAULT_NAME}-month-tag`} />
                      );
                    }

                    // 最多显示前 7 个日期，超过的用 ... 代替
                    const maxShow = 7;
                    const showList = selected.slice(0, maxShow);
                    const hasMore = selected.length > maxShow;
                    const text =
                      showList.map(String).join('、') + (hasMore ? '、...' : '');

                    return (
                      <span
                        className={`${DEFAULT_NAME}-month-tag`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        {text}
                      </span>
                    );
                  }}
                />
              )}
              {triggerType !== 'once' && (
                <TimePicker
                  className={`${DEFAULT_NAME}-timepicker`}
                  value={triggerTime ? dayjs(triggerTime, 'HH:mm') : null}
                  onChange={(d) => setTriggerTime(d)}
                  format="HH:mm"
                  placeholder={intl.t('选择执行时间')}
                  allowClear={false}
                  needConfirm={false}
                  showNow={false}
                  defaultOpenValue={getDefaultTriggerTime()}
                  suffixIcon={<Icon icon={eed_web_icon_global_03_action_xialaxiao_area_log} size={16} />}
                  getPopupContainer={() => document.getElementsByClassName(`${DEFAULT_NAME}-timepicker`)[0] as HTMLElement}
                />)}
            </div>
          </div>
        </div>

        <div className={`${DEFAULT_NAME}-field`}>
          <span className={`${DEFAULT_NAME}-label`}>
            {intl.t('到期时间 *')}
          </span>
          <DatePicker
            className={`${DEFAULT_NAME}-expiration-datepicker`}
            value={expireTime}
            onChange={(d) => setExpireTime(d)}
            placeholder={intl.t('选择到期时间')}
            format="YYYY-MM-DD HH:mm"
            allowClear={false}
            needConfirm={false}
            showNow={false}
            showTime={{ showNow: false, defaultOpenValue: getDefaultTriggerTime() }}
            locale={datePickerLocale}
            disabledDate={(d) => d != null && d.endOf('day').isBefore(dayjs().startOf('day'))}
            suffixIcon={<Icon icon={eed_web_icon_global_03_action_xialaxiao_area_log} size={16} />}
            getPopupContainer={() => document.getElementsByClassName(`${DEFAULT_NAME}-expiration-datepicker`)[0] as HTMLElement}
          />
        </div>
      </div>

      <div className={`${DEFAULT_NAME}-footer`}>
        {configId && (
          <Button
            className={`${DEFAULT_NAME}-footer-delete`}
            type="primary"
            danger={true}
            ghost={true}
            autoInsertSpace={false}
            onClick={handleDeleteClick}
          >
            {intl.t('mario.button.delete')}
          </Button>
        )}
        <div className={`${DEFAULT_NAME}-footer-actions`}>
          <Button
            className={`${DEFAULT_NAME}-footer-cancel`}
            autoInsertSpace={false}
            onClick={handleClose}>
            {intl.t('mario.button.cancel')}
          </Button>
          <Button
            className={`${DEFAULT_NAME}-footer-confirm`}
            type="primary"
            autoInsertSpace={false}
            onClick={handleConfirm}
            color="default">
            {intl.t('确定')}
          </Button>
        </div>
      </div>

      <ConfirmDeleteModal
        visible={showConfirmDelete}
        onClose={() => setShowConfirmDelete(false)}
        onConfirm={handleDeleteConfirm}
      />
    </div >
  );
};

export default OperationSchedulerModal;
